import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

@Component({
  selector: 'cs-tab',
  templateUrl: './cs-tab.component.html',
  styleUrls: ['./cs-tab.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class CSTabComponent implements AfterViewInit, OnDestroy {
  constructor(private cdr: ChangeDetectorRef) {}

  private _selectedIndex = 0;
  public currentPadding = '';
  public currentfontWeight = 'fw-medium';

  @Input() tabs: string[] = [];
  @Input() tabIcons: string[] = [];
  @Input() tabImages: string[] = [];
  @Input() width: 'full' | 'auto' = 'full';
  @Input() direction: 'horizontal' | 'vertical' = 'horizontal';
  @Input() textAlign: 'left' | 'center' | 'right' = 'center';

  @Input() set selectedIndex(value: number) {
    this._selectedIndex = value;
    this.queueUpdateBackground();
  }
  get selectedIndex() {
    return this._selectedIndex;
  }

  @Input() set padding(value: string) {
    this.currentPadding = `p-${value}`;
  }
  @Input() set fontWeight(value: 'normal' | 'medium' | 'semibold' | 'bold') {
    this.currentfontWeight = `fw-${value}`;
  }

  @Output() tabChange = new EventEmitter<number>();

  @ViewChildren('tabBtn') tabBtns!: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('group', { static: true }) groupRef!: ElementRef<HTMLDivElement>;

  @HostBinding('style.width') get hostWidth() {
    return this.width === 'full' ? '100%' : 'auto';
  }

  backgroundPos = 0;
  backgroundSize = 0;

  private ro?: ResizeObserver;
  private rafId: number | null = null;

  ngAfterViewInit() {
    // 初始對齊
    setTimeout(() => {
      this.updateBackground();
      this.cdr.detectChanges();
    });

    // 監控所有 tab 尺寸/位置變動
    this.ro = new ResizeObserver(() => this.queueUpdateBackground());
    this.tabBtns.changes.subscribe(() => {
      this.tabBtns.forEach(ref => this.ro!.observe(ref.nativeElement));
      this.queueUpdateBackground();
    });
    this.tabBtns.forEach(ref => this.ro!.observe(ref.nativeElement));
  }

  ngOnDestroy() {
    this.ro?.disconnect();
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }

  @HostListener('window:resize')
  onResize() {
    this.queueUpdateBackground();
  }

  selectTab(index: number) {
    this.selectedIndex = index;
    this.tabChange.emit(index);
    this.scrollSelectedIntoCenter();
  }

  /** 在下一個 frame 計算，避免動畫中量測造成誤差 */
  private queueUpdateBackground() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.updateBackground();
      this.rafId = null;
    });
  }

  private updateBackground() {
    if (!this.tabBtns?.length) return;

    const btn = this.tabBtns.get(this.selectedIndex);
    if (!btn) return;

    const el = btn.nativeElement;
    const container = this.groupRef.nativeElement;

    const styles = getComputedStyle(container);
    const padLeft = parseFloat(styles.paddingLeft || '0');
    const padTop = parseFloat(styles.paddingTop || '0');

    if (this.direction === 'horizontal') {
      // offsetLeft 包含 paddingLeft，扣掉可得相對於背景起點的位置
      this.backgroundPos = el.offsetLeft - padLeft;
      this.backgroundSize = el.offsetWidth;
    } else {
      this.backgroundPos = el.offsetTop - padTop;
      this.backgroundSize = el.offsetHeight;
    }
  }

  private scrollSelectedIntoCenter() {
    if (this.direction !== 'horizontal') return;
    if (!this.tabBtns?.length) return;

    const btn = this.tabBtns.get(this.selectedIndex);
    if (!btn) return;

    const container = this.findScrollableParent(this.groupRef?.nativeElement ?? null) ?? this.groupRef?.nativeElement;
    if (!container) return;

    // 使用 rAF 讓 DOM 完成重算後再取位移
    requestAnimationFrame(() => {
      const btnRect = btn.nativeElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offset = btnRect.left + btnRect.width / 2 - (containerRect.left + containerRect.width / 2);
      const nextLeft = Math.min(
        Math.max(container.scrollLeft + offset, 0),
        container.scrollWidth - container.clientWidth,
      );

      container.scrollTo({ left: nextLeft, behavior: 'smooth' });
    });
  }

  private findScrollableParent(el: HTMLElement | null): HTMLElement | null {
    let current: HTMLElement | null = el;
    while (current) {
      const style = getComputedStyle(current);
      const overflowX = style.overflowX || style.overflow;
      const isScrollable =
        (overflowX === 'auto' || overflowX === 'scroll') && current.scrollWidth > current.clientWidth;
      if (isScrollable) return current;
      current = current.parentElement;
    }
    return null;
  }
}
